/**
 * in-game-menu.ts — the overlay shown when Esc is pressed in-game with
 * nothing else to dismiss. Per UI_UX §9.3 flow:
 *
 *   Esc → Menu
 *     ├─ Resume
 *     ├─ Save Game            (v2M2 — opens slot picker)
 *     ├─ Settings             (stubbed until v2M4)
 *     ├─ Return to Main Menu  (discard-with-confirm in v2M1)
 *     └─ Exit
 *
 * Mounted on demand by the session and torn down on Resume or on any
 * transition that leaves the in-game mode. Style mirrors main-menu.ts so
 * the two feel like the same system.
 */

import { getAudioEngine } from '../audio';

export interface InGameMenuCallbacks {
  onResume(): void;
  /** v2M2 — opens a Save slot picker. Optional so tests / sessions without saving wire can omit it. */
  onSaveGame?: () => void;
  /**
   * v4M1.4 — combined "Save & Return to Main Menu" action. Opens the
   * save picker; on successful save, tears down the session and
   * returns to the main menu without re-prompting for confirmation
   * (the explicit Save & Return click is the consent). Optional so
   * sessions without saving wire can omit the button.
   */
  onSaveAndReturn?: () => void;
  onSettings(): void;
  onReturnToMainMenu(): void;
  /**
   * v3M12.4 — manual surrender. Triggers the `concede` command which
   * flips `gameOutcome` to `'defeat'`. Confirm gating happens at the
   * dispatch site (session.ts), matching the Return-to-Main-Menu
   * pattern.
   */
  onConcede(): void;
  onExit(): void;
}

export interface InGameMenuHandle {
  destroy(): void;
}

export function mountInGameMenu(
  parent: HTMLElement,
  callbacks: InGameMenuCallbacks,
): InGameMenuHandle {
  const overlay = document.createElement('div');
  overlay.id = 'in-game-menu-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    // v4M1.11 — 150 ms opacity fade on mount + destroy. Initial value is
    // 0; a queued microtask flips it to 1 so the transition fires on the
    // first paint after the element joins the document.
    opacity: '0',
    transition: 'opacity 150ms ease-out',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minWidth: '260px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'PAUSED';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '10px',
    textAlign: 'center',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  const makeButton = (
    label: string,
    onClick: () => void,
    tooltip?: string,
  ): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    if (tooltip) btn.title = tooltip;
    Object.assign(btn.style, {
      padding: '12px 20px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '14px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
      textAlign: 'left',
    } as Partial<CSSStyleDeclaration>);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#172031';
      btn.style.borderColor = '#3F4C66';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#0E131C';
      btn.style.borderColor = '#2A3344';
    });
    btn.addEventListener('click', onClick);
    card.appendChild(btn);
    return btn;
  };

  // Confirm prompts moved out to the callback dispatch site (main.ts +
  // session.ts) so the v2M4 Settings → "Confirm Exit" / "Confirm Return"
  // toggles can gate them. The menu now just dispatches raw intent.
  // v4M1.2 — per-button tooltips (UI_UX §6).
  makeButton('Resume', callbacks.onResume, 'Close this menu and resume play.');
  if (callbacks.onSaveGame) {
    makeButton(
      'Save Game',
      callbacks.onSaveGame,
      'Save the current game to a slot. Pauses while the slot picker is open.',
    );
  }
  if (callbacks.onSaveAndReturn) {
    // v4M1.4 — combined Save & Return action.
    makeButton(
      'Save & Return to Main Menu',
      callbacks.onSaveAndReturn,
      'Open the save picker; once a save lands, return to the main menu. No confirmation prompt — the explicit Save & Return click is the consent.',
    );
  }
  makeButton('Settings', callbacks.onSettings, 'Open the Settings overlay.');
  makeButton(
    'Concede',
    callbacks.onConcede,
    'Surrender immediately. Flips the game outcome to Defeat; the frozen final state stays viewable.',
  );
  makeButton(
    'Return to Main Menu',
    callbacks.onReturnToMainMenu,
    'Leave the current game and return to the title screen. Unsaved progress is discarded after confirmation.',
  );
  makeButton('Exit', callbacks.onExit, 'Close the application.');

  parent.appendChild(overlay);
  // v4M1.11 — kick off the fade-in. requestAnimationFrame defers to the
  // next paint so the browser commits the initial opacity:0 before the
  // transition target lands.
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });
  getAudioEngine().playCue('ui:panel-open');

  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      // v4M1.11 — 150 ms fade-out. The overlay is unmounted after the
      // transition completes; ramping opacity to 0 first gives the
      // visual handoff back to the sim canvas a graceful tail.
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 150);
    },
  };
}
